// **الوضع الفاتح والداكن** (قرار المالك 21/09/2026: «عاوز البرنامج يشتغل على مود dark, light»).
//
// ثلاث حالات لا اثنتان: **فاتح** و**داكن** و**تلقائي** — والتلقائي يتبع إعداد الجهاز نفسه،
// فمن ضبط هاتفه على الداكن ليلًا يجد البرنامج معه بلا أن يضبط شيئًا ثانيًا.
//
// والاختيار يُحفظ في المتصفّح لا في الخادم: تفضيلُ عينٍ لا بيانُ حساب، ويختلف بين جهاز
// الموظف في المكتب وهاتفه في البيت — فلا معنى لتوحيده عبر الأجهزة.
//
// **والصنف يُوضع على `<html>` قبل أول رسم** بسطرٍ في `index.html` لا هنا: لو انتظر React
// لرأى المستخدم ومضةً بيضاء ثم اسودّت الشاشة. وهذا الملف يتولّى ما بعد ذلك — التبديل،
// والاستماع لتغيّر إعداد الجهاز، ولون شريط المتصفّح.
import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'auto';

/** مفتاح الحفظ — **نفسه** في سطر `index.html`. أيُّ تغييرٍ هنا يُغيَّر هناك. */
export const THEME_KEY = 'icontravel.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** لون شريط المتصفّح والنظام حول الصفحة — من لون ورقة البرنامج في كل وضع. */
const BAR = { light: '#ffffff', dark: '#0f172a' };

const isTheme = (v: unknown): v is Theme => v === 'light' || v === 'dark' || v === 'auto';

/** المحفوظ، وإلّا «تلقائي». وقراءة التخزين قد ترمي في وضع التصفّح الخاص — فتُحاط. */
export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (isTheme(v)) return v;
  } catch { /* تخزينٌ ممنوع: التلقائي تصرّفٌ سليم لا خطأ */ }
  return 'auto';
}

/** هل النتيجة داكنة؟ «تلقائي» يسأل الجهاز. */
export function resolveDark(theme: Theme): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  try { return window.matchMedia(DARK_QUERY).matches; } catch { return false; }
}

/** وضعُ الصنف على `<html>` وضبط لون الشريط — نقطةُ التأثير الوحيدة على الصفحة. */
export function applyTheme(theme: Theme): void {
  const dark = resolveDark(theme);
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  // `color-scheme` تُلوّن ما لا يملكه CSS: أشرطة التمرير، ومنتقي التاريخ، وحقول النظام
  root.style.colorScheme = dark ? 'dark' : 'light';
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? BAR.dark : BAR.light);
}

/**
 * الوضع الحالي ومبدِّله. والاستماع لتغيّر إعداد الجهاز **في حالة «تلقائي» وحدها**:
 * من اختار بيده لا يُنقض اختيارُه لأن الشمس غربت.
 */
export function useTheme(): { theme: Theme; dark: boolean; setTheme: (t: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(readTheme);
  const [dark, setDark] = useState(() => resolveDark(readTheme()));

  useEffect(() => {
    applyTheme(theme);
    setDark(resolveDark(theme));
    if (theme !== 'auto') return;
    let mq: MediaQueryList;
    try { mq = window.matchMedia(DARK_QUERY); } catch { return; }
    const on = () => { applyTheme('auto'); setDark(mq.matches); };
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    try { localStorage.setItem(THEME_KEY, t); } catch { /* يبقى الاختيار لهذه الجلسة */ }
    setThemeState(t);
  }, []);

  return { theme, dark, setTheme };
}
